import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useOverwatchMapTripAnnotationsQuery } from '../Annotations.hook'
import {
  ANNOTATION_PAGE_SIZE,
  type AnnotationListSignature,
  annotationListSignature,
  appendAnnotationPage,
  isSameAnnotationList,
  MAX_ANNOTATION_PAGES,
} from './annotation-paging'
import type { TripAnnotationMarker } from './types'

export type UseTripAnnotationsOptions = {
  /**
   * Narrow the layer to one trip / one VTR. Both default to null, which
   * trip-manager reads as "the most recent annotations fleet-wide" — and that is
   * the form worth defaulting to: an annotation marks a place a driver had
   * trouble, which is a property of the road and the approach to it rather than
   * of whichever trip happened to be running, so scoping the layer to the trip
   * on screen hides the notes most worth seeing.
   *
   * It is also the only form that returns anything against real data today:
   * every annotation in production carries neither a trip_id nor a vtr_id,
   * because the uploading client has never populated either field, so a
   * trip- or VTR-scoped query matches nothing at all in any environment. The
   * parameters exist so a caller can scope the layer once that changes.
   */
  tripId?: string | null
  vtrId?: string | null
  /**
   * Scope the fleet-wide form to one metropolis (fleet-manager id). Without it
   * trip-manager returns the newest 500 annotations across every city, so a
   * busy metro crowds out the one the map is showing. Ignored when `tripId` or
   * `vtrId` is set.
   */
  metropolisId?: number | null
  /** Don't fetch at all — for a caller that mounts the layer behind a toggle or a permission. */
  skip?: boolean
  /**
   * Also fetch each annotation's ADR-0029 classification.
   *
   * Off by default, and gated in the document with `@include(if:)` rather than
   * always selected, because only the triage page reads a classification — the
   * other two callers draw markers and a popup. Leaving it on for them would add
   * an eleven-field object per annotation to a fleet-wide response nothing
   * renders.
   *
   * With this false, `TripAnnotationMarker.classification` is `undefined` rather
   * than `null`: not asked for, as distinct from asked for and absent.
   */
  withClassification?: boolean
  /**
   * Also fetch each annotation's speaker turns and transcript-correction
   * metadata (ADR-0034).
   *
   * Off by default and gated with `@include(if:)` for the same reason as
   * {@link withClassification}: only the triage page's edit dialog reads them.
   * With this false, `TripAnnotationMarker.transcriptTurns` and
   * `transcriptCorrection` are `undefined` rather than empty or `null`.
   */
  withTranscriptEditing?: boolean
  /**
   * Keep fetching older pages after the first until the fleet-wide list is
   * exhausted or {@link MAX_ANNOTATION_PAGES} pages are loaded.
   *
   * Off by default: the map layers want the newest page only, and each extra
   * page is another round trip. The triage tabs turn it on because they
   * summarise the whole history, and a window of the newest 500 is dominated by
   * whichever city recorded most recently, so a chart or table built from it
   * says little about the fleet. Ignored when `tripId` or `vtrId` is set, since
   * trip-manager returns those unpaged.
   *
   * The first page is returned as soon as it lands, and the rest are appended
   * as they arrive.
   */
  fetchAllPages?: boolean
}

export type UseTripAnnotationsResult = {
  annotations: TripAnnotationMarker[]
  /** The first page is still loading. */
  loading: boolean
  /** Older pages are still arriving, so `annotations` is not yet the whole list. */
  loadingMore: boolean
  /**
   * `annotations` may be missing older rows: pages are still loading, loading
   * stopped on an error or the page cap, or `fetchAllPages` is off and the
   * first page came back full.
   */
  truncated: boolean
}

/**
 * Fetches driver voice notes and maps them to the shape the annotation layer
 * draws ({@link TripAnnotationMarker}).
 *
 * Owned by this lib rather than by either consuming area so both maps fetch the
 * same fields through the same operation — the popup and marker code below read
 * `hasAudio`, `transcriptionStatus` and `vtrId`, and an area that assembled its
 * own query would silently render a degraded popup the moment it forgot one.
 * Rows are passed through unfiltered; {@link useAnnotationsState} validates
 * coordinates and applies the user's filters.
 */
export const useTripAnnotations = ({
  tripId = null,
  vtrId = null,
  metropolisId = null,
  skip = false,
  withClassification = false,
  withTranscriptEditing = false,
  fetchAllPages = false,
}: UseTripAnnotationsOptions = {}): UseTripAnnotationsResult => {
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const isScoped = Boolean(tripId || vtrId)
  const { data, loading, fetchMore } = useErrorHandlingQuery(
    useOverwatchMapTripAnnotationsQuery,
    {
      variables: {
        tripId,
        vtrId,
        metropolisId,
        withClassification,
        withTranscriptEditing,
        limit: ANNOTATION_PAGE_SIZE,
        offset: 0,
      },
      skip,
    },
  )

  const loadedCount = data?.tripAnnotations.length ?? 0
  const [loadingMore, setLoadingMore] = useState(false)
  const [stoppedEarly, setStoppedEarly] = useState(false)
  const inFlight = useRef(false)
  const exhausted = useRef(false)
  // Requests made since paging last started over. Counted rather than derived
  // from the rows loaded: a page that overlaps rows already loaded adds fewer
  // than a full page, so rows alone would let a churning list outrun the cap.
  const pagesFetched = useRef(0)
  const seen = useRef<{
    variables: string
    signature: AnnotationListSignature
  } | null>(null)
  const variablesKey = [
    tripId,
    vtrId,
    metropolisId,
    withClassification,
    withTranscriptEditing,
  ].join('|')

  useEffect(() => {
    if (!fetchAllPages || isScoped || skip || loading) return
    // Paging starts over when the query changes or its base page is replaced
    // (a refetch after a save, say), judged from the rows rather than the
    // request: a refetch of an exactly full list returns the same count, so
    // only the ends of the list show that something moved.
    const rows = data?.tripAnnotations ?? []
    if (
      seen.current &&
      (seen.current.variables !== variablesKey ||
        !isSameAnnotationList(seen.current.signature, rows))
    ) {
      exhausted.current = false
      pagesFetched.current = 0
      setStoppedEarly(false)
    }
    seen.current = {
      variables: variablesKey,
      signature: annotationListSignature(rows),
    }
    if (loadedCount < ANNOTATION_PAGE_SIZE) return
    if (exhausted.current || inFlight.current) return
    // The first page came with the query, so this counts the pages after it.
    if (pagesFetched.current + 1 >= MAX_ANNOTATION_PAGES) {
      exhausted.current = true
      setStoppedEarly(true)
      return
    }

    inFlight.current = true
    pagesFetched.current += 1
    setLoadingMore(true)
    let grew = false
    let pageSize = 0
    fetchMore({
      variables: { offset: loadedCount, limit: ANNOTATION_PAGE_SIZE },
      updateQuery: (previous, { fetchMoreResult }) => {
        const page = fetchMoreResult?.tripAnnotations ?? []
        pageSize = page.length
        const merged = appendAnnotationPage(previous.tripAnnotations, page)
        grew = merged.length > previous.tripAnnotations.length
        return { ...previous, tripAnnotations: merged }
      },
    })
      .then(() => {
        // A short page is the end of the list. A full page that added nothing is
        // a backend that ignores `offset` (triage filters off), so asking again
        // would repeat the same page forever.
        if (pageSize < ANNOTATION_PAGE_SIZE || !grew) {
          exhausted.current = true
          setStoppedEarly(pageSize >= ANNOTATION_PAGE_SIZE)
        }
      })
      .catch(error => {
        exhausted.current = true
        setStoppedEarly(true)
        enqueueMoiaErrorNotification(error)
      })
      .finally(() => {
        inFlight.current = false
        setLoadingMore(false)
      })
  }, [
    fetchAllPages,
    isScoped,
    skip,
    loading,
    data,
    variablesKey,
    loadedCount,
    fetchMore,
    enqueueMoiaErrorNotification,
  ])

  const annotations = useMemo(
    () =>
      data?.tripAnnotations.map(
        (annotation): TripAnnotationMarker => ({
          id: annotation.annotationId,
          recordedAtMs: annotation.recordedAtMs,
          vehicleId: annotation.vehicleId,
          driverId: annotation.driverId,
          severity: annotation.severity,
          location: annotation.location,
          transcript: annotation.transcript,
          transcriptionStatus: annotation.transcriptionStatus,
          // `audioS3Uri` is nullable: null while a voicebot recording is still
          // being built, or when there is none. See `TripAnnotationMarker.hasAudio`.
          hasAudio: Boolean(annotation.audioS3Uri),
          tripId: annotation.tripId,
          vtrId: annotation.vtrId,
          answeredAtMs: annotation.answeredAtMs,
          answeredBy: annotation.answeredBy,
          resolution: annotation.resolution
            ? {
                resolvedAtMs: annotation.resolution.resolvedAtMs,
                resolvedBy: annotation.resolution.resolvedBy,
                evidenceType: annotation.resolution.evidenceType,
                evidenceRef: annotation.resolution.evidenceRef,
                note: annotation.resolution.note,
              }
            : null,
          jiraIssueKey: annotation.jiraIssueKey ?? null,
          // Passed through rather than flattened, because the object is the unit
          // that is nullable: ADR-0029 keeps classification in a child table, so
          // an annotation either has a current labeling event or it has none.
          // Spreading it into sibling fields would turn one honest "not
          // classified" into eleven independently-null ones — which is the exact
          // reasoning the contract itself gives for the shape ("hence one
          // nullable object, not several independently-nullable fields").
          //
          // Note the resolver fails closed: if `category` or `source` doesn't map
          // to a GraphQL enum it returns null for the *whole* object rather than
          // erroring, so an unrecognised category reaches the UI as "not
          // classified" rather than as "unknown category".
          //
          // `undefined` when the caller left `withClassification` off, because
          // the field is then absent from the response entirely. That is a
          // different fact from `null`, and the type keeps them apart.
          classification: annotation.classification,
          // Same three-state contract as `classification`: `undefined` when
          // `withTranscriptEditing` is off, because the fields are then absent
          // from the response.
          transcriptTurns: annotation.transcriptTurns,
          transcriptCorrection: annotation.transcriptCorrection,
        }),
      ) ?? [],
    [data],
  )

  const truncated = fetchAllPages
    ? loadingMore || stoppedEarly
    : loadedCount >= ANNOTATION_PAGE_SIZE

  return { annotations, loading, loadingMore, truncated }
}
